import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';

import { getTotalFunds } from '../../services/api';
import { MoneyFlowRange, TotalFundsDto } from '../../types/types';
import { AnimatedNumber } from './animatedNumber';
import { EmptyState } from './emptyState';
import { RANGE_OPTIONS } from './moneyFlowChart';
import { Skeleton } from './skeleton';

interface FundsSegment {
  labelKey: string;
  amount: number;
  percent: number;
  barClass: string;
  dotClass: string;
}

const getSegments = (funds: TotalFundsDto): FundsSegment[] => [
  {
    labelKey: 'paid',
    amount: funds.paid,
    percent: funds.paidPercent,
    barClass: 'bg-blue-400',
    dotClass: 'bg-blue-500',
  },
  {
    labelKey: 'refunded',
    amount: funds.refunded,
    percent: funds.refundedPercent,
    barClass: 'bg-red-400',
    dotClass: 'bg-red-400',
  },
  {
    labelKey: 'project_costs',
    amount: funds.projectCosts,
    percent: funds.projectCostsPercent,
    barClass: 'bg-red-300',
    dotClass: 'bg-red-300',
  },
  {
    labelKey: 'remaining_balance',
    amount: funds.remainingBalance,
    percent: funds.remainingPercent,
    barClass: 'bg-green-400',
    dotClass: 'bg-green-400',
  },
];

const ALL_RANGES: MoneyFlowRange[] = RANGE_OPTIONS.map(({ value }) => value);

export const TotalFundsCard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const [range, setRange] = useState<MoneyFlowRange>('WEEK');
  const [funds, setFunds] = useState<TotalFundsDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [barReady, setBarReady] = useState<boolean>(false);

  // Responses per range. Switching to a range we've already seen is instant,
  // and we still refresh it silently in the background.
  const cacheRef = useRef<Partial<Record<MoneyFlowRange, TotalFundsDto>>>({});
  const hasPrefetchedRef = useRef<boolean>(false);

  useEffect(() => {
    let isCancelled = false;
    const cached = cacheRef.current[range];

    if (cached) {
      setFunds(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    getTotalFunds(range)
      .then((data) => {
        cacheRef.current[range] = data;
        if (!isCancelled) setFunds(data);

        // After the first successful load, warm up the other ranges in the background
        if (!hasPrefetchedRef.current) {
          hasPrefetchedRef.current = true;
          ALL_RANGES.filter((other) => other !== range).forEach((other) => {
            getTotalFunds(other)
              .then((otherData) => {
                cacheRef.current[other] = otherData;
              })
              .catch(() => undefined);
          });
        }
      })
      .catch(() => {
        if (!isCancelled && !cached) {
          setFunds(null);
          toast.error(t('failed_to_load_total_funds'));
        }
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [range, t]);

  // Bar grows from 0 on first load, then smoothly morphs between ranges
  useEffect(() => {
    if (!funds || isLoading) {
      setBarReady(false);
      return;
    }
    const frame = requestAnimationFrame(() => setBarReady(true));
    return () => cancelAnimationFrame(frame);
  }, [funds, isLoading]);

  const currencySymbol = !funds?.currency || funds.currency === 'EUR' ? '€' : funds.currency;
  const formatMoney = (amount: number): string =>
    `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currencySymbol}`;

  const isEmpty = !isLoading && (!funds || funds.totalFunds === 0);
  const showData = !isLoading && !!funds && !isEmpty;

  return (
    <div className="flex h-full min-w-0 flex-col gap-4 rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5 font-['Plus_Jakarta_Sans',sans-serif]">
      <p className="text-sm font-medium leading-5 text-gray-900">{t('total_funds')}</p>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {isLoading ? (
            <Skeleton className="h-10 w-44" />
          ) : (
            <p className="truncate text-4xl font-extrabold leading-10 text-gray-900 tabular-nums">
              {showData && funds ? (
                <AnimatedNumber value={funds.totalFunds} format={formatMoney} />
              ) : (
                ''
              )}
            </p>
          )}
        </div>

        <div className="inline-flex w-fit shrink-0 items-center gap-0.5 rounded-3xl bg-gray-100 px-2 py-1">
          {RANGE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={range === value}
              onClick={() => setRange(value)}
              className={`rounded-3xl px-3 py-1.5 text-sm font-medium leading-5 transition-all duration-200 active:scale-95 ${
                range === value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div role="status" aria-busy="true" className="flex flex-col gap-4">
          <Skeleton className="h-12 w-full rounded-full" />
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-3 rounded-full" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-3 w-24" />
              </div>
            ))}
          </div>
        </div>
      )}

      {isEmpty && <EmptyState text={t('no_data_to_display')} />}

      {showData && funds && (
        <div className="flex flex-col gap-4 motion-safe:animate-[fadeIn_0.4s_ease-out]">
          <div className="flex h-12 w-full overflow-hidden rounded-full">
            {getSegments(funds).map(({ labelKey, amount, percent, barClass }) => (
              <div
                key={labelKey}
                className={`flex items-center justify-center overflow-hidden whitespace-nowrap text-sm font-semibold text-gray-900 transition-[width] duration-700 ease-out ${barClass}`}
                style={{ width: barReady ? `${percent}%` : '0%' }}
              >
                {percent > 0 && (
                  <span
                    className={`tabular-nums transition-opacity duration-500 ${
                      barReady ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <AnimatedNumber value={amount} format={formatMoney} />
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {getSegments(funds).map(({ labelKey, amount, percent, dotClass }) => (
              <div key={labelKey} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-gray-900">
                  <span className={`h-3 w-3 shrink-0 rounded-full ${dotClass}`} />
                  <span className="truncate">{t(labelKey)}</span>
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-gray-900">
                  <AnimatedNumber value={amount} format={formatMoney} /> •{' '}
                  <AnimatedNumber value={percent} format={(n) => `${n.toFixed(0)}%`} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
